import { useId } from 'react'

import { PERIOD_PRESET_OPTIONS } from './orders-list-filter-options'
import { resolveOrderPeriod } from './orders-list-filters'
import type { OrderListValues } from './orders-list-filters'
import type { OrderPeriod } from './orders-list-filter-options'
import { Input } from '@/components/ui/input'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import { getPresetRange } from '@/lib/date-presets'
import { m } from '@/lib/paraglide/messages'
import { twJoin } from '@/lib/utils'

interface Props {
    value: OrderListValues
    patch: (partial: Partial<OrderListValues>) => void
    surface: 'bar' | 'sheet'
}

/**
 * Ô "Thời gian" kiểu KiotViet: một dòng chọn nhanh (mặc định Tháng này) và một
 * dòng Tùy chỉnh. Hai ô ngày luôn hiện đúng khoảng ĐANG áp dụng — người dùng
 * thấy "Tháng này" là từ ngày nào tới ngày nào; sửa một ô là chuyển sang tùy
 * chỉnh, ô còn lại giữ mốc đang có.
 */
export function OrdersPeriodField({ value, patch, surface }: Props) {
    const name = useId()
    const isCustom = value.period === 'custom'
    const range = resolveOrderPeriod(value)
    const height = surface === 'sheet' ? 'h-11' : 'h-9'

    const pickPreset = (period: OrderPeriod) =>
        patch({ period, date_from: '', date_to: '' })
    const setDate = (key: 'date_from' | 'date_to', next: string) =>
        patch({
            period: 'custom',
            date_from: range?.from ?? '',
            date_to: range?.to ?? '',
            [key]: next,
        })

    return (
        <fieldset className="grid gap-2">
            <legend className="mb-1 text-sm font-medium">
                {m.order_period_label()}
            </legend>
            <div className="flex items-center gap-2">
                <input
                    type="radio"
                    name={name}
                    className="accent-primary size-4 shrink-0"
                    aria-label={m.order_period_preset_aria()}
                    checked={!isCustom}
                    onChange={() => pickPreset('month')}
                />
                <Select
                    value={isCustom ? '' : value.period}
                    onValueChange={(next) =>
                        pickPreset(
                            PERIOD_PRESET_OPTIONS().find(
                                (o) => o.value === next,
                            )?.value ?? 'month',
                        )
                    }
                >
                    <SelectTrigger
                        aria-label={m.order_period_preset_aria()}
                        className={twJoin(height, 'w-full')}
                    >
                        <SelectValue placeholder={m.order_period_label()} />
                    </SelectTrigger>
                    <SelectContent>
                        {PERIOD_PRESET_OPTIONS().map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>
                                {opt.label}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>
            <label className="flex items-center gap-2 text-sm">
                <input
                    type="radio"
                    name={name}
                    className="accent-primary size-4 shrink-0"
                    checked={isCustom}
                    onChange={() => {
                        // Từ "Toàn thời gian" chưa có khoảng nào để giữ — lấy
                        // Tháng này làm điểm xuất phát, nếu không URL rỗng lại
                        // thành mặc định và ô Tùy chỉnh bấm như không.
                        const start = range ?? getPresetRange('month')
                        patch({
                            period: 'custom',
                            date_from: start.from,
                            date_to: start.to,
                        })
                    }}
                />
                {m.order_period_custom()}
            </label>
            {/* Cột trái hẹp: hai ô ngày cạnh nhau bị cắt chữ → xếp dọc. */}
            <div
                className={twJoin(
                    'grid gap-2 pl-6',
                    surface === 'sheet' ? 'grid-cols-2' : 'grid-cols-1',
                )}
            >
                <Input
                    type="date"
                    aria-label={m.report_from_date()}
                    className={height}
                    value={range?.from ?? ''}
                    onChange={(e) => setDate('date_from', e.target.value)}
                />
                <Input
                    type="date"
                    aria-label={m.report_to_date()}
                    className={height}
                    value={range?.to ?? ''}
                    onChange={(e) => setDate('date_to', e.target.value)}
                />
            </div>
        </fieldset>
    )
}
